import { useEffect, useState } from "react";
import { Disc3, Plus, Search, X } from "lucide-react";
import { api, posterUrl } from "@/lib/api";
import { CheckChip } from "@/components/CheckChip";

export const MONEY = [
  { key: "subscription", label: "Subscription" },
  { key: "free", label: "Free" },
  { key: "rent", label: "Rent" },
  { key: "buy", label: "Buy" },
];

let providerCache = null;
let providerPromise = null;
export function useProviders() {
  const [data, setData] = useState(providerCache);
  useEffect(() => {
    if (providerCache) return;
    providerPromise = providerPromise || api.get("/providers").then(({ data: d }) => { providerCache = d; return d; });
    providerPromise.then(setData).catch(() => { providerPromise = null; });
  }, []);
  return data;
}

export const watchOn = (w) => w?.mode === "specific" && (w.providers.length > 0 || w.money.length > 0 || w.physical);

export function watchSummary(w) {
  if (!watchOn(w)) return null;
  const left = new Set(w.providers);
  const names = [];
  for (const g of providerCache?.groups || []) {
    if (g.ids.every((i) => left.has(i))) { names.push(g.name); g.ids.forEach((i) => left.delete(i)); }
  }
  for (const id of left) names.push(providerCache?.all.find((p) => p.id === id)?.name || "1 more service");
  if (w.physical) names.push("DVD/Blu-ray");
  const money = MONEY.filter((m) => w.money.includes(m.key)).map((m) => m.label).join("/");
  return [names.join(", ") || "Any service", money].filter(Boolean).join(" · ");
}

const Logo = ({ path }) =>
  path ? <img src={posterUrl(path, "w92")} alt="" className="h-7 w-7 rounded-lg object-cover shrink-0" /> : <span className="h-7 w-7 rounded-lg bg-secondary shrink-0" />;

function ModeSwitch({ mode, onChange, prefix }) {
  return (
    <div className="flex rounded-full bg-secondary p-1 w-fit">
      {[["any", "Don't care"], ["specific", "Specific"]].map(([m, label]) => (
        <button key={m} type="button" onClick={() => onChange(m)} data-testid={`${prefix}-watch-mode-${m}`} aria-pressed={mode === m}
          className={`h-9 px-5 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function ProviderSearch({ data, chosen, onAdd, prefix }) {
  const [q, setQ] = useState("");
  const grouped = new Set(data.groups.flatMap((g) => g.ids));
  const term = q.trim().toLowerCase();
  const hits = term ? data.all.filter((p) => !grouped.has(p.id) && !chosen.includes(p.id) && p.name.toLowerCase().includes(term)).slice(0, 8) : [];
  return (
    <div className="mt-4">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="More services… (search ~300)" data-testid={`${prefix}-provider-search`}
          className="w-full h-11 pl-10 pr-4 rounded-2xl border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/50" />
      </div>
      {term && (
        <ul className="mt-2 rounded-2xl border bg-card divide-y overflow-hidden">
          {hits.length === 0 && <li className="px-4 py-3 text-sm text-muted-foreground">No other services match.</li>}
          {hits.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => { onAdd(p.id); setQ(""); }} data-testid={`${prefix}-provider-search-result-${p.id}`}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-left hover:bg-accent">
                <Logo path={p.logo_path} /> <span className="flex-1 truncate">{p.name}</span> <Plus className="h-4 w-4 text-primary" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Services({ data, w, set, prefix }) {
  const ids = new Set(w.providers);
  const grouped = new Set(data.groups.flatMap((g) => g.ids));
  const extras = w.providers.filter((id) => !grouped.has(id));
  const toggleGroup = (g) => {
    const on = g.ids.every((i) => ids.has(i));
    set({ providers: on ? w.providers.filter((i) => !g.ids.includes(i)) : [...new Set([...w.providers, ...g.ids])] });
  };
  return (
    <div>
      <p className="text-sm font-semibold mb-3">Services <span className="text-muted-foreground font-normal">· matches any checked service</span></p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
        {data.groups.map((g) => (
          <CheckChip key={g.key} on={g.ids.every((i) => ids.has(i))} onClick={() => toggleGroup(g)} testId={`${prefix}-provider-${g.key}`} className="py-2.5 px-3 gap-2 text-[13px]">
            <span className="flex items-center gap-2 min-w-0"><Logo path={g.logo_path} /><span className="truncate">{g.name}</span></span>
          </CheckChip>
        ))}
      </div>
      {extras.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {extras.map((id) => {
            const p = data.all.find((x) => x.id === id);
            return (
              <span key={id} className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full border border-primary bg-primary/10 text-sm font-semibold" data-testid={`${prefix}-provider-extra-${id}`}>
                <Logo path={p?.logo_path} /> {p?.name || id}
                <button type="button" onClick={() => set({ providers: w.providers.filter((x) => x !== id) })} aria-label="Remove" data-testid={`${prefix}-provider-extra-remove-${id}`}>
                  <X className="h-4 w-4" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <ProviderSearch data={data} chosen={w.providers} onAdd={(id) => set({ providers: [...w.providers, id] })} prefix={prefix} />
    </div>
  );
}

export function WatchFilter({ value: w, onChange, prefix }) {
  const data = useProviders();
  const set = (patch) => onChange({ ...w, ...patch });
  const toggleMoney = (k) => set({ money: w.money.includes(k) ? w.money.filter((m) => m !== k) : [...w.money, k] });
  return (
    <section data-testid={`${prefix}-watch-section`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display font-bold text-lg">How to watch</h3>
        <ModeSwitch mode={w.mode} onChange={(mode) => set({ mode })} prefix={prefix} />
      </div>
      {w.mode === "any" ? (
        <p className="text-sm text-muted-foreground rounded-2xl border border-dashed p-4" data-testid={`${prefix}-watch-any-note`}>
          Any service or format.
          {watchOn({ ...w, mode: "specific" }) && " Your saved services are kept — switch to Specific to use them again."}
        </p>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Loading services…</p>
      ) : (
        <div className="space-y-8">
          <Services data={data} w={w} set={set} prefix={prefix} />
          <div>
            <p className="text-sm font-semibold mb-3">Physical media</p>
            <CheckChip on={w.physical} onClick={() => set({ physical: !w.physical })} testId={`${prefix}-physical`} className="w-full sm:w-auto">
              <span className="flex items-center gap-2.5"><Disc3 className="h-5 w-5 text-muted-foreground" /> DVD / Blu-ray <span className="text-xs text-muted-foreground font-normal">released on disc</span></span>
            </CheckChip>
          </div>
          <div>
            <p className="text-sm font-semibold mb-3">$ Cost <span className="text-muted-foreground font-normal">· {w.money.length ? "a checked service must offer it this way" : "none checked = any"}</span></p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {MONEY.map((m) => (
                <CheckChip key={m.key} on={w.money.includes(m.key)} onClick={() => toggleMoney(m.key)} testId={`${prefix}-money-${m.key}`}>{m.label}</CheckChip>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">US availability by JustWatch via TMDB.</p>
        </div>
      )}
    </section>
  );
}
